import * as React from 'react';
import { isEqual } from 'src/utils/lodash-alternatives';

import cx from 'clsx';
import styles from './AssetLayer.module.scss';

interface ImageType {
  normal: string;
  retina?: string;
  modern?: {
    normal: string;
    retina?: string;
  };
}
interface ImageAsset {
  type: 'image';
  imageSrc?: ImageType;
  rounded?: 'all' | 'top' | 'none';
  sizedSources?: {
    min?: number;
    max?: number;
    src: ImageType;
  }[];
  alt?: string;
  isLazy?: boolean;
  loadingAnimation?: boolean;
  fallbackBackgroundColor?: string;
  imageAttributes?: {
    [key: string]: boolean | string | { [key: string]: string };
  };
}

interface VideoAsset {
  type: 'video';
  videoSrc?: string;
  rounded?: 'all' | 'top' | 'none';
  sizedSources?: {
    min?: number;
    max?: number;
    src: ImageType;
  }[];
  alt?: string;
  isLazy?: boolean;
  loadingAnimation?: boolean;
  fallbackBackgroundColor?: string;
  videoAttributes?: {
    [key: string]: boolean | string | { [key: string]: string };
  };
}
type AssetLayerProps = ImageAsset | VideoAsset;

const createImageSrcSet = (imageNormal?: string, imageRetina?: string) => {
  if (!imageRetina && imageNormal) return imageNormal;
  else if (imageRetina && imageNormal) {
    return `${imageNormal} 1x, ${imageRetina} 2x`;
  } else return '';
};

export const createSourceMedia = (
  src: ImageType,
  min?: number,
  max?: number
) => {
  let media = '';
  if (min) media = `(min-width: ${min}px)`;
  if (min && max) media = `${media} and `;
  if (max) media = `${media}(max-width: ${max}px)`;

  const isAvailableModernFormat = src && src.modern;
  const srcNormal = isAvailableModernFormat
    ? `${src.modern?.normal}`
    : src.normal;
  const srcRetina = isAvailableModernFormat
    ? `${src.modern?.retina}`
    : src.retina;
  const srcSet = createImageSrcSet(srcNormal, srcRetina);
  const type = isAvailableModernFormat ? { type: 'image/webp' } : {};

  return {
    media,
    srcSet,
    ...type,
  };
};

const createAsset = ({
  intersected,
  ref,
  loadingAnimation,
  fallbackBackgroundColor,
  type,
  alt,
  attributes,
  sizedSources,
  videoSrc,
  imageSrc,
}: {
  intersected: boolean;
  ref: any;
  loadingAnimation?: boolean;
  fallbackBackgroundColor: string;
  alt?: string;
  type: 'video' | 'image';
  attributes?: { [key: string]: boolean | string | { [key: string]: string } };
  videoSrc?: string;
  sizedSources?: {
    min?: number;
    max?: number;
    src: {
      normal: string;
      retina?: string;
      modern?: {
        normal: string;
        retina?: string;
      };
    };
  }[];
  imageSrc?: {
    normal: string;
    retina?: string;
    modern?: {
      normal: string;
      retina?: string;
    };
  };
}) => {
  if (!intersected) {
    return (
      <img
        alt={'asset'}
        ref={ref}
        className={cx(styles.notVisible, loadingAnimation && styles.animation)}
        style={{ backgroundColor: fallbackBackgroundColor || '' }}
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      />
    );
  }

  if (!sizedSources && !imageSrc) {
    switch (type) {
      case 'video':
        return (
          <video
            className={cx(styles.video, {
              [styles.animation]: loadingAnimation,
            })}
            style={{ backgroundColor: fallbackBackgroundColor || '' }}
            data-testid="asset-video-element"
            ref={ref}
          />
        );
      case 'image':
        return (
          <img
            className={cx(styles.image, {
              [styles.animation]: loadingAnimation,
            })}
            style={{ backgroundColor: fallbackBackgroundColor || '' }}
            {...attributes}
            data-testid="asset-image-element"
            alt={alt}
            ref={ref}
          />
        );
    }
  }

  let srcSet = createImageSrcSet(imageSrc?.normal, imageSrc?.retina);
  let sizes = '';

  // support for safari where picture tag is not supported
  if (sizedSources) {
    srcSet = '';
    sizes = '';
    const isRetina = Number(window.devicePixelRatio) > 1;
    sizedSources.forEach(s => {
      const imgSrc = isRetina ? s.src.retina || s.src.normal : s.src.normal;

      srcSet = `${srcSet}, ${imgSrc} ${s.max || s.min}w`;
      sizes = `${sizes} ${
        s.max ? `(max-width: ${s.max}px) ${Math.floor(s.max / 2)}px,` : ``
      } ${s.min ? `(min-width: ${s.min}px) ${s.min}px,` : ``}`;
    });
  }

  const isAvailableModernFormat = imageSrc && imageSrc.modern;

  const returnImagesInModernFormat = () => {
    return sizedSources ? (
      sizedSources.map((src, i) => (
        <source
          key={i}
          data-testid={`asset-image-source-element-${i}`}
          {...createSourceMedia(src.src, src.min, src.max)}
        />
      ))
    ) : (
      <source
        srcSet={
          imageSrc &&
          createImageSrcSet(imageSrc.modern?.normal, imageSrc.modern?.retina)
        }
        type="image/webp"
        data-testid="asset-image-source-element"
      />
    );
  };

  switch (type) {
    case 'video':
      return (
        <video
          className={cx(styles.video, {
            [styles.animation]: loadingAnimation,
          })}
          style={{ backgroundColor: fallbackBackgroundColor || '' }}
          data-testid="asset-video-element"
          src={videoSrc}
          {...attributes}
          ref={ref}
        />
      );
    case 'image':
      return (
        <picture className={styles.picture}>
          {isAvailableModernFormat ? returnImagesInModernFormat() : undefined}
          {sizedSources
            ? sizedSources.map((src, i) => (
                <source
                  key={i}
                  {...createSourceMedia(src.src, src.min, src.max)}
                />
              ))
            : undefined}
          <img
            className={cx(styles.image, {
              [styles.animation]: loadingAnimation,
            })}
            style={{ backgroundColor: fallbackBackgroundColor || '' }}
            {...attributes}
            data-testid="asset-image-element"
            alt={alt}
            srcSet={srcSet}
            src={
              intersected
                ? sizedSources
                  ? sizedSources[0].src.normal
                  : imageSrc?.normal
                : undefined
            }
            sizes={sizes}
            ref={ref}
          />
        </picture>
      );
  }
};

const AssetLayer: React.FC<AssetLayerProps> = React.memo(
  props => {
    const {
      fallbackBackgroundColor = 'rgb(0, 20, 60)',
      rounded = 'all',
      isLazy,
    } = props;
    const [intersected, setIntersected] = React.useState<boolean>(false);
    const ref: any = React.useRef();

    React.useEffect(() => {
      if (!('IntersectionObserver' in window) || !isLazy) {
        setIntersected(true);
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIntersected(true);
          }
        },
        { threshold: 0.01 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }
      return () => {
        if (observer && observer.disconnect) observer.disconnect();
      };
    }, [ref, isLazy]);
    return (
      <div
        className={cx(styles.wrapper, {
          [styles.rounded]: rounded === 'all',
          [styles.roundedTop]: rounded === 'top',
        })}
      >
        {createAsset({
          ...props,
          intersected,
          ref,
          fallbackBackgroundColor,
          attributes:
            props.type === 'image'
              ? props.imageAttributes
              : props.videoAttributes,
        })}
      </div>
    );
  },
  (prevProps: any, nextProps: any) => isEqual(prevProps, nextProps)
);

export default AssetLayer;
